import { REQUEST_METHOD } from '@/constants';
import { API_ENDPOINTS } from '@/constants/api-endpoints';
import {
  ICreateWorkspaceRequest,
  ICreateWorkspaceResponse,
  ICreditAnalytics,
  ICreditTransaction,
  ICreditUsageHistory,
  IJoinWorkspaceRequest,
  ISendInvitationRequest,
  ISwitchWorkspaceRequest,
  IUpdateMemberPermissionsRequest,
  IWorkspace,
  IWorkspaceCredits,
  IWorkspaceInvitation,
  IWorkspaceListItem,
  IWorkspaceMember,
  IWorkspaceWithMembership,
} from '@/types/workspace';
import { apiInstance } from '@/utils';

// ====================================================================
// Core Workspace Operations
// ====================================================================

export const getWorkspaces = async (): Promise<{ workspaces: IWorkspaceListItem[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.listWorkspaces,
  });

  return result.data;
};

export const getActiveWorkspace = async (): Promise<{
  activeWorkspace: IWorkspaceWithMembership;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getActiveWorkspace,
  });

  return result.data;
};

export const getWorkspaceById = async (
  workspaceId: number,
): Promise<{ workspace: IWorkspaceWithMembership }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getWorkspace(workspaceId),
  });

  return result.data;
};

export const createWorkspace = async (
  data: ICreateWorkspaceRequest,
): Promise<ICreateWorkspaceResponse> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.createWorkspace,
  });

  return result.data;
};

export const updateWorkspace = async (
  workspaceId: number,
  data: Partial<IWorkspace>,
): Promise<{ message: string; workspace: IWorkspace }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    data,
    url: API_ENDPOINTS.updateWorkspace(workspaceId),
  });

  return result.data;
};

export const deleteWorkspace = async (workspaceId: number): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.deleteWorkspace(workspaceId),
  });

  return result.data;
};

// ====================================================================
// Workspace Switching & Joining
// ====================================================================

export const switchWorkspace = async (
  data: ISwitchWorkspaceRequest,
): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.switchWorkspace,
  });

  return result.data;
};

export const joinWorkspace = async (
  data: IJoinWorkspaceRequest,
): Promise<{
  message: string;
  workspace?: IWorkspaceWithMembership;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url: API_ENDPOINTS.joinWorkspace,
  });

  return result.data;
};

// ====================================================================
// Member Management
// ====================================================================

export const getWorkspaceMembers = async (
  workspaceId: number,
): Promise<{ members: IWorkspaceMember[] }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url: API_ENDPOINTS.getWorkspaceMembers(workspaceId),
  });

  return result.data;
};

export const updateMemberPermissions = async (
  workspaceId: number,
  memberId: string,
  data: IUpdateMemberPermissionsRequest,
): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.PATCH,
    data,
    url: API_ENDPOINTS.updateMemberPermissions(workspaceId, memberId),
  });

  return result.data;
};

export const removeMember = async (
  workspaceId: number,
  memberId: string,
): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.removeMember(workspaceId, memberId),
  });

  return result.data;
};

export const requestCreditAccess = async (workspaceId?: number): Promise<{ message: string }> => {
  const url = workspaceId
    ? `${API_ENDPOINTS.requestCreditAccess}?workspaceId=${workspaceId}`
    : API_ENDPOINTS.requestCreditAccess;

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url,
  });

  return result.data;
};

// ====================================================================
// Admin Management (Multi-Admin Feature)
// ====================================================================

export const promoteToAdmin = async (
  workspaceId: number,
  userId: string,
): Promise<{ message: string; user: IWorkspaceMember }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.promoteToAdmin(workspaceId, userId),
  });

  return result.data;
};

export const demoteFromAdmin = async (
  workspaceId: number,
  userId: string,
): Promise<{ message: string; user: IWorkspaceMember }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.demoteFromAdmin(workspaceId, userId),
  });

  return result.data;
};

// ====================================================================
// Join Code Management
// ====================================================================

export const generateJoinCode = async (
  workspaceId: number,
): Promise<{ message: string; join_code: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.generateJoinCode(workspaceId),
  });

  return result.data;
};

// ====================================================================
// Credit Management
// ====================================================================

export const getWorkspaceCredits = async (
  workspaceId?: number,
): Promise<{ credits: IWorkspaceCredits }> => {
  const url = workspaceId
    ? API_ENDPOINTS.getWorkspaceCreditsById(workspaceId)
    : API_ENDPOINTS.getWorkspaceCredits;

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url,
  });

  return result.data;
};

export const planCreditConsumption = async (
  creditsNeeded: number,
  workspaceId?: number,
): Promise<{
  can_consume: boolean;
  workspace_id: number;
  credits_needed: number;
  consumption_plan: {
    free_credits_to_use: number;
    paid_credits_to_use: number;
  };
  workspace_credits_before: {
    free_credits: number;
    paid_credits: number;
    total: number;
  };
  workspace_credits_after: {
    free_credits: number;
    paid_credits: number;
    total: number;
  };
}> => {
  const url = workspaceId
    ? `${API_ENDPOINTS.planCreditConsumption}?workspaceId=${workspaceId}`
    : API_ENDPOINTS.planCreditConsumption;

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { creditsNeeded },
    url,
  });

  return result.data;
};

export const consumeCredits = async (
  creditsNeeded: number,
  featureName: string,
  metadata?: Record<string, any>,
  workspaceId?: number,
): Promise<{
  success: boolean;
  message: string;
  usage_id: number;
  credits_consumed: {
    free_credits_used: number;
    paid_credits_used: number;
    total_credits_used: number;
  };
  credits_after: {
    free_credits: number;
    paid_credits: number;
    total: number;
  };
}> => {
  const url = workspaceId
    ? `${API_ENDPOINTS.consumeCredits}?workspaceId=${workspaceId}`
    : API_ENDPOINTS.consumeCredits;

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { creditsNeeded, featureName, metadata },
    url,
  });

  return result.data;
};

export const addCredits = async (
  creditType: 'free' | 'paid',
  amount: number,
  description?: string,
  workspaceId?: number,
): Promise<{ message: string; new_balance: { free_credits: number; paid_credits: number } }> => {
  const url = workspaceId
    ? `${API_ENDPOINTS.addCredits}?workspaceId=${workspaceId}`
    : API_ENDPOINTS.addCredits;

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { creditType, amount, description },
    url,
  });

  return result.data;
};

export const getCreditHistory = async (
  params?: {
    page?: number;
    limit?: number;
    startDate?: string;
    endDate?: string;
    creditType?: 'free' | 'paid';
    feature?: string;
  },
  workspaceId?: number,
): Promise<{
  history: ICreditUsageHistory[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}> => {
  const url = workspaceId
    ? API_ENDPOINTS.getCreditHistory.replace('/credits/', `/${workspaceId}/credits/`)
    : API_ENDPOINTS.getCreditHistory;

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params,
    url,
  });

  return result.data;
};

export const getCreditAnalytics = async (
  params?: {
    startDate?: string;
    endDate?: string;
  },
  workspaceId?: number,
): Promise<{ analytics: ICreditAnalytics }> => {
  const url = workspaceId
    ? API_ENDPOINTS.getCreditAnalytics.replace('/credits/', `/${workspaceId}/credits/`)
    : API_ENDPOINTS.getCreditAnalytics;

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params,
    url,
  });

  return result.data;
};

export const getCreditTransactions = async (
  params?: {
    page?: number;
    limit?: number;
    transactionType?: string;
  },
  workspaceId?: number,
): Promise<{
  transactions: ICreditTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}> => {
  const url = workspaceId
    ? API_ENDPOINTS.getCreditTransactions.replace('/credits/', `/${workspaceId}/credits/`)
    : API_ENDPOINTS.getCreditTransactions;

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    params,
    url,
  });

  return result.data;
};

export const purchaseCredits = async (
  packageId: string,
  workspaceId?: number,
): Promise<{ checkoutUrl: string }> => {
  const url = workspaceId
    ? `${API_ENDPOINTS.purchaseCredits}?workspaceId=${workspaceId}`
    : API_ENDPOINTS.purchaseCredits;

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data: { packageId },
    url,
  });

  return result.data;
};

// ====================================================================
// Invitation Management
// ====================================================================

export const sendInvitation = async (
  data: ISendInvitationRequest,
  workspaceId?: number,
): Promise<{ message: string; invitation: IWorkspaceInvitation }> => {
  const url = workspaceId
    ? API_ENDPOINTS.sendInvitationToWorkspace(workspaceId)
    : API_ENDPOINTS.sendInvitation;

  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    data,
    url,
  });

  return result.data;
};

export const getWorkspaceInvitations = async (
  workspaceId?: number,
): Promise<{ invitations: IWorkspaceInvitation[] }> => {
  const url = workspaceId
    ? API_ENDPOINTS.getWorkspaceInvitations(workspaceId)
    : API_ENDPOINTS.getInvitations;

  const result = await apiInstance({
    method: REQUEST_METHOD.GET,
    url,
  });

  return result.data;
};

export const revokeInvitation = async (invitationId: number): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.DELETE,
    url: API_ENDPOINTS.revokeInvitation(invitationId),
  });

  return result.data;
};

export const resendInvitation = async (invitationId: number): Promise<{ message: string }> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.resendInvitation(invitationId),
  });

  return result.data;
};

export const acceptInvitation = async (
  token: string,
): Promise<{
  message: string;
  workspace: IWorkspaceWithMembership;
}> => {
  const result = await apiInstance({
    method: REQUEST_METHOD.POST,
    url: API_ENDPOINTS.acceptInvitation(token),
  });

  return result.data;
};
